import { FC, ReactNode } from 'react';
import { Box, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

interface PanelProps {
  children: ReactNode;
  sx?: SxProps<Theme>;
}

/** A bordered white surface used across the admin panel (matches the design's `Panel`). */
export const AdminPanel: FC<PanelProps> = observer(({ children, sx }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
});

interface PanelHeadProps {
  icon?: ReactNode;
  iconColor?: string;
  title: ReactNode;
  sub?: ReactNode;
  right?: ReactNode;
}

/** The header row of an {@link AdminPanel}: icon chip, title/subtitle and an optional right slot. */
export const AdminPanelHead: FC<PanelHeadProps> = observer(({ icon, iconColor, title, sub, right }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        px: 2.25,
        py: 1.875,
        display: 'flex',
        alignItems: 'center',
        gap: 1.375,
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
        flexShrink: 0,
        flexWrap: 'wrap',
      }}
    >
      {icon && (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2.25,
            backgroundColor: theme.palette.grey[200],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: iconColor ?? theme.palette.text.secondary,
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 120 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: theme.palette.text.primary }}>
          {title}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: 11.5, color: theme.palette.text.tertiary, mt: 0.125 }}>
            {sub}
          </Typography>
        )}
      </Box>
      {right}
    </Box>
  );
});
